// AdminLayout — sticky sidebar (#1E3A5F) + top header with admin name/role/logout.
// Hidden from viewers: links to /admin/admins and /admin/audit (super_admin only).
import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { adminAxios, useAdminAuth } from './AdminAuthContext';
import {
  LayoutDashboard, Users, ShieldCheck, ScrollText, LogOut, Eye,
  CircleDollarSign, Bell, Activity, UserCog, Mail, X,
} from 'lucide-react';

const NAV = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/users', label: 'Usuários', icon: Users },
  { to: '/admin/usage', label: 'Uso', icon: Activity },
  { to: '/admin/transactions', label: 'Financeiro', icon: CircleDollarSign },
  { to: '/admin/alerts', label: 'Alertas', icon: Bell, badgeKey: 'alerts' },
  { to: '/admin/admins', label: 'Admins', icon: ShieldCheck, superOnly: true },
  { to: '/admin/audit', label: 'Auditoria', icon: ScrollText, superOnly: true },
  { to: '/admin/profile', label: 'Meu perfil', icon: UserCog },
];

const AdminLayout = () => {
  const { admin, logout, isSuper, token } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [alertCount, setAlertCount] = useState(0);
  const [me, setMe] = useState(null);
  const [bannerHidden, setBannerHidden] = useState(false);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await adminAxios(token).get('/api/admin/alerts?status_filter=novo');
        if (!cancelled) setAlertCount(r.data?.counts?.novo ?? 0);
      } catch { /* silent */ }
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await adminAxios(token).get('/api/admin/me');
        if (!cancelled) setMe(r.data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [token, location.pathname]);

  // Force change-password screen if backend says so (except on the change-password route itself).
  if (admin?.must_change_password && !location.pathname.endsWith('/admin/change-password')) {
    navigate('/admin/change-password', { replace: true });
  }

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Sidebar */}
      <aside
        className="hidden md:flex flex-col w-64 bg-[#1E3A5F] text-white shrink-0"
        data-testid="admin-sidebar"
      >
        <div className="px-5 py-5 border-b border-white/10">
          <Link to="/admin/dashboard" className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-white" />
            <div>
              <p className="text-base font-black leading-tight">Tô de Olho</p>
              <p className="text-[10px] uppercase tracking-wider text-white/60">Painel admin</p>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.filter(item => !item.superOnly || isSuper).map(({ to, label, icon: Icon, badgeKey }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded text-sm font-medium transition-colors ${
                  isActive ? 'bg-white/15 text-white' : 'text-white/75 hover:bg-white/10 hover:text-white'
                }`
              }
              data-testid={`admin-nav-${to.split('/').pop()}`}
            >
              <Icon className="w-4 h-4" />
              <span className="flex-1">{label}</span>
              {badgeKey === 'alerts' && alertCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-bold"
                      data-testid="admin-nav-alerts-badge">
                  {alertCount > 99 ? '99+' : alertCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <p className="text-[11px] text-white/60">Logado como</p>
          <p className="text-sm font-bold">{admin?.nome}</p>
          <p className="text-[10px] text-white/60">{admin?.email}</p>
          <span className="inline-flex items-center mt-1.5 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-white/15">
            {admin?.role === 'super_admin' ? 'Super Admin' : 'Visualizador'}
          </span>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-[#E2E8F0] px-4 py-3 flex items-center justify-between">
          <div className="md:hidden">
            <Link to="/admin/dashboard" className="text-[#1E3A5F] font-black text-base">
              Tô de Olho
            </Link>
          </div>
          <div className="flex items-center gap-3 ml-auto">
            <div className="text-right">
              <p className="text-sm font-semibold text-[#0F1C2E]" data-testid="admin-header-name">
                {admin?.nome}
              </p>
              <p className="text-[10px] text-[#475569]">
                {admin?.role === 'super_admin' ? 'Super Admin' : 'Visualizador'}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium bg-[#F4F6F8] text-[#1E3A5F] hover:bg-[#E2E8F0]"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" /> Sair
            </button>
          </div>
        </header>
        {/* Recovery email banner — only shown when admin has not registered/verified */}
        {me && !bannerHidden && !me.email_recuperacao_verificado
          && !location.pathname.endsWith('/admin/profile') && (
          <div className="bg-[#FEF3C7] border-b border-[#FDE68A] px-4 md:px-6 py-2.5"
               data-testid="admin-recovery-banner">
            <div className="flex items-center gap-3 max-w-5xl">
              <Mail className="w-4 h-4 text-[#92400E] shrink-0" />
              <p className="text-xs text-[#92400E] flex-1">
                {me.tem_email_recuperacao
                  ? `Confirme seu e-mail de recuperação (${me.email_recuperacao_mascarado}). O link foi enviado, expira em 24 horas.`
                  : 'Cadastre um e-mail de recuperação para proteger sua conta. Sem ele, recuperar o acesso depende de outro Super Admin.'}
              </p>
              <Link
                to="/admin/profile"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold bg-[#92400E] text-white hover:bg-[#7C2D12]"
                data-testid="admin-recovery-banner-cta"
              >
                {me.tem_email_recuperacao ? 'Reenviar / alterar' : 'Cadastrar agora'}
              </Link>
              <button
                onClick={() => setBannerHidden(true)}
                aria-label="Fechar"
                className="text-[#92400E] hover:text-[#7C2D12]"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
        <main className="flex-1 p-4 md:p-6 overflow-y-auto">
          <Outlet />
        </main>
        {/* Mobile bottom nav (super sees more items so split into 2 rows on small screens). */}
        <nav className="md:hidden grid grid-cols-4 border-t border-[#E2E8F0] bg-white">
          {NAV.filter(item => !item.superOnly || isSuper).slice(0, 4).map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 py-2 text-[10px] font-medium ${
                  isActive ? 'text-[#1E3A5F]' : 'text-[#94A3B8]'
                }`
              }
            >
              <Icon className="w-4 h-4" /> {label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default AdminLayout;
